import React from "react";
import { motion } from "framer-motion";
import { Check, Lock, Play, Star, Flame, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";
import RUNNER from "../assets/journey/runner.png";
import PEAK from "../assets/journey/peak.png";
import MILESTONE from "../assets/journey/milestone.png";

const GAP = 138;
const TOP = 70;

const fmtTime = (s) => {
  const n = Math.max(0, Math.round(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};
// Wider left/right swing → a proper winding "adventure trail" feel.
const LEFT = 28;
const RIGHT = 72;

/**
 * Winding "Fitness Journey" trail. Renders a smooth S-curve SVG path (a
 * tan trail bed under a gold "trodden" trail) with alternating left/right
 * nodes (completed / active / locked / peak) layered on top, plus a runner
 * character bobbing on the active step. Data comes from the /journey API
 * (levels of the current world) mapped into `nodes` by Journey.jsx.
 */
export const JourneyPath = ({ nodes = [], activeIndex, milestone }) => {
  const { t } = useTranslation();
  if (!nodes.length) return null;

  const H = TOP + nodes.length * GAP + 12;
  const xOf = (i) => (i % 2 === 0 ? LEFT : RIGHT);
  const yOf = (i) => TOP + i * GAP;
  const pts = nodes.map((_, i) => ({ x: xOf(i), y: yOf(i) }));

  // Smooth cubic-bezier S-curves between consecutive node centres. Control
  // points sit half a gap above/below so each segment eases in and out —
  // guaranteed to pass exactly through the node centres (nodes stay "on
  // the line") while the path visibly winds.
  const segOf = (p1, p2) =>
    `M ${p1.x} ${p1.y} C ${p1.x} ${p1.y + GAP / 2} ${p2.x} ${p2.y - GAP / 2} ${p2.x} ${p2.y}`;

  const fullPath = pts.slice(0, -1).map((p1, i) => segOf(p1, pts[i + 1])).join(" ");

  const segments = pts.slice(0, -1).map((p1, i) => {
    const p2 = pts[i + 1];
    const travelled = nodes[i + 1].state === "completed" || nodes[i + 1].state === "active";
    return { d: segOf(p1, p2), travelled, key: i };
  });

  return (
    <section data-testid="journey-path" className="relative w-full pt-3 pb-2">
      <div className="relative w-full" style={{ height: H }}>
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 100 ${H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* Trail bed — soft tan "ground" beneath the whole trail. */}
          <path
            d={fullPath}
            fill="none"
            stroke="#DED5C2"
            strokeWidth={11}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.55}
            vectorEffect="non-scaling-stroke"
          />
          {/* Locked (not-yet-travelled) trail: dashed sage. Drawn first so
              the gold travelled segments sit crisply on top. */}
          {segments.filter((s) => !s.travelled).map((s) => (
            <path
              key={`l-${s.key}`}
              d={s.d}
              fill="none"
              stroke="#C4CFC0"
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeDasharray="1 9"
              vectorEffect="non-scaling-stroke"
            />
          ))}
          {/* Travelled trail: warm gold, drawn on to reveal along the path. */}
          {segments.filter((s) => s.travelled).map((s) => (
            <motion.path
              key={`t-${s.key}`}
              d={s.d}
              fill="none"
              stroke="#E0A15C"
              strokeWidth={5}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1 + s.key * 0.12 }}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        {/* Milestone badge floated near the first travelled segment */}
        {milestone && pts.length >= 2 && (
          <motion.div
            data-testid="journey-milestone-badge"
            className="absolute z-20 flex flex-col items-center -translate-x-1/2 -translate-y-1/2"
            style={{ left: `${(pts[0].x + pts[1].x) / 2}%`, top: (pts[0].y + pts[1].y) / 2 }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 200, damping: 12 }}
          >
            <img src={MILESTONE} alt="" aria-hidden="true" className="w-14 h-14 object-contain drop-shadow-md" />
            <span className="mt-0.5 text-[10px] font-bold text-[#B07B3C] whitespace-nowrap">
              {t("dashboard.milestone_n", "Milestone {{n}}!", { n: milestone.day })}
            </span>
          </motion.div>
        )}

        {nodes.map((node, i) => (
          <PathNode key={node.key} node={node} x={pts[i].x} y={pts[i].y} index={i} isActive={i === activeIndex} />
        ))}
      </div>
    </section>
  );
};

function PathNode({ node, x, y, index, isActive }) {
  const { t } = useTranslation();
  const isPeak = node.kind === "peak";
  const size = node.state === "active" ? 78 : 58;

  const bubble = (() => {
    if (isPeak) {
      const done = node.state === "completed";
      return (
        <div
          className="relative grid place-items-center rounded-2xl border shadow-md"
          style={{
            width: 74,
            height: 74,
            background: done
              ? "linear-gradient(160deg,#EFF1EC 0%,#DDE3D6 100%)"
              : "linear-gradient(160deg,#FFFFFF 0%,#F4F1E8 100%)",
            borderColor: done ? "rgba(95,113,97,0.45)" : "#E6E2D8",
          }}
        >
          {/* gold pedestal ring */}
          <span className="absolute inset-0 rounded-2xl ring-2 ring-[#E0A15C]/40" />
          <img src={PEAK} alt="" aria-hidden="true" className="w-14 h-14 object-contain" />
        </div>
      );
    }
    if (node.state === "completed") {
      return (
        <div
          className="relative grid place-items-center rounded-full text-white shadow-md"
          style={{
            width: size,
            height: size,
            background: "linear-gradient(150deg,#6E8470 0%,#556A57 100%)",
          }}
        >
          <span className="absolute inset-1 rounded-full ring-1 ring-white/30" />
          <Check size={24} strokeWidth={2.6} />
        </div>
      );
    }
    if (node.state === "active") {
      return (
        <motion.div
          className="relative grid place-items-center rounded-full text-white shadow-lg"
          style={{
            width: size,
            height: size,
            background: "linear-gradient(145deg,#F0B072 0%,#D9924E 100%)",
          }}
          animate={{ boxShadow: ["0 0 0 0 rgba(224,161,92,0.5)", "0 0 0 16px rgba(224,161,92,0)"] }}
          transition={{ duration: 1.8, repeat: Infinity }}
        >
          <span className="absolute inset-[3px] rounded-full ring-2 ring-white/45" />
          <Play size={28} strokeWidth={2.2} className="ml-0.5" fill="currentColor" />
        </motion.div>
      );
    }
    return (
      <div
        className="grid place-items-center rounded-full border shadow-sm"
        style={{
          width: size,
          height: size,
          background: "linear-gradient(160deg,#FFFFFF 0%,#F1F3EE 100%)",
          borderColor: "#DFE3DA",
          opacity: 0.85,
        }}
      >
        <Lock size={18} strokeWidth={1.8} className="text-[#9DB0A1]" />
      </div>
    );
  })();

  const clickable = node.state === "active" || (node.state === "completed" && node.onClick);

  return (
    <div
      className="absolute z-10 -translate-x-1/2 -translate-y-1/2"
      data-journey-node={node.key}
      style={{ left: `${x}%`, top: y, width: 104 }}
    >
      <motion.div
        className="relative flex flex-col items-center"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 + index * 0.08, type: "spring", stiffness: 220, damping: 15 }}
      >
        {isActive && (
          <motion.img
            src={RUNNER}
            alt=""
            aria-hidden="true"
            className="absolute -top-[54px] w-14 h-14 object-contain drop-shadow-md pointer-events-none z-20"
            animate={{ y: [-3, 3, -3] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}
        <div className="relative">
          {/* soft ground shadow → nodes appear to float above the trail */}
          <span
            aria-hidden="true"
            className="absolute left-1/2 -translate-x-1/2 -bottom-1 rounded-full bg-black/15 blur-[4px]"
            style={{ width: size * 0.7, height: 7 }}
          />
          <button
            data-testid={node.state === "active" ? "path-node-active-play" : `path-node-${index}`}
            onClick={clickable ? node.onClick : undefined}
            disabled={!clickable}
            aria-label={node.label}
            className={`relative transition-transform ${clickable ? "active:scale-90 cursor-pointer" : "cursor-default"}`}
          >
            {bubble}
          </button>
        </div>
        <p
          style={{ fontFamily: "Outfit, sans-serif" }}
          className={`mt-1.5 text-xs font-bold text-center leading-tight ${
            node.state === "locked" ? "text-[#8AA48F]" : "text-[#2C332A]"
          }`}
        >
          {node.label}
        </p>
        {node.sub && <p className="text-[10px] text-[#2C332A]/55 text-center leading-tight">{node.sub}</p>}
        {node.state === "completed" && node.kind !== "peak" && (
          <div className="flex items-center gap-0.5 mt-0.5" data-testid={`node-stars-${index}`}>
            {[1, 2, 3].map((s) => (
              <Star
                key={s}
                size={11}
                strokeWidth={1.5}
                className={s <= (node.stars || 0) ? "text-[#E8A365]" : "text-[#D8DCD3]"}
                fill={s <= (node.stars || 0) ? "#E8A365" : "none"}
              />
            ))}
          </div>
        )}
        {node.state === "completed" && node.kind !== "peak" && node.calories > 0 && (
          <span
            data-testid={`node-kcal-${index}`}
            className="mt-0.5 inline-flex items-center gap-0.5 text-[9px] font-bold text-[#B07B3C] leading-none"
          >
            <Flame size={9} strokeWidth={2} fill="#E8A365" className="text-[#E8A365]" />
            {node.calories} kcal
          </span>
        )}
        {node.state === "completed" && node.kind !== "peak" && node.bestTimeSec != null && (
          <span
            data-testid={`node-best-time-${index}`}
            className="mt-0.5 inline-flex items-center gap-0.5 rounded-full bg-[#EFF1EC] px-1.5 py-0.5 text-[9px] font-bold text-[#5F7161] leading-none"
            title={t("journey.beat_this_time", "Replay to beat your best")}
          >
            <Timer size={9} strokeWidth={2.2} className="text-[#5F7161]" />
            {t("journey.best_prefix", "PB")} {fmtTime(node.bestTimeSec)}
          </span>
        )}
      </motion.div>
    </div>
  );
}

export default JourneyPath;
